import { Component, ElementRef, Injector, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import {
  AbstractComponent,
  AlertModalComponent,
  ConfirmationComponent,
  FirebaseService,
  Funnel,
  FunnelService,
  Importation,
  ImportationService,
  ImportationTypeEnum,
  UploadTypeEnum,
  UtilHelper,
} from 'lib-trend-core';
import * as Papa from 'papaparse';
import { takeUntil } from 'rxjs';

type CardField = 'name' | 'phone' | 'customer' | 'ignore';

interface MappingRow {
  csvColumn: string;
  sample: string;
  mappedTo: CardField;
  idFunnel?: string;
}

@Component({
    selector: 'app-card-upload',
    templateUrl: './card-upload.component.html',
    styleUrl: './card-upload.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CardUploadComponent extends AbstractComponent {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('inputFile', { static: false }) inputFile: ElementRef<HTMLInputElement>;

  public stepIndex = signal(0);

  public file: File | undefined;
  public fileLoaded = false;
  public url = '';
  public rawCsvText: string = undefined;

  public rawRows: Record<string, any>[] = [];
  public rawHeaders: string[] = [];
  public mappings: MappingRow[] = [];

  public csvContent: any[] = [];
  public paginatedContent: any[] = [];
  public pageSize = 5;
  public currentPage = 0;
  public showOnlyErrors = false;

  public availableFields: { value: CardField; label: string }[] = [
    { value: 'name', label: 'kanban.cardUpload.fieldName' },
    { value: 'customer', label: 'kanban.cardUpload.fieldCustomer' },
    { value: 'phone', label: 'kanban.cardUpload.fieldPhone' },
    { value: 'ignore', label: 'kanban.cardUpload.fieldIgnore' },
  ];

  public funnelList: Funnel[] = [];

  constructor(
    injector: Injector,
    public dialogRef: MatDialogRef<CardUploadComponent>,
    public dialog: MatDialog,
    private firebaseService: FirebaseService,
    private importationService: ImportationService,
    private funnelService: FunnelService,
  ) {
    super(injector);
    this.setupForm();
  }

  ngOnInit(): void {
    this.getFunnels();
  }

  private setupForm(): void {
    this.formGroup = this.formBuilder.group({
      funnel: [null, [Validators.required]],
    });

    this.formGroup.valueChanges.subscribe(value => {
      if (!value.funnel) {
        this.csvContent = [];
        this.paginatedContent = [];
      }
    });
  }

  next(): void {
    if (this.canGoNext()) this.stepIndex.update(i => i + 1);
  }

  prev(): void {
    if (this.stepIndex() > 0) this.stepIndex.update(i => i - 1);
  }

  canGoNext(): boolean {
    if (this.stepIndex() === 0) return !!this.fileLoaded && !this.loading;
    if (this.stepIndex() === 1) return this.isMappingValid();
    return true;
  }

  onContinueFromUpload(): void {
    if (!this.canGoNext()) return;
    this.parseCsvAndStoreRaw();
    this.next();
  }

  onContinueFromMapping(): void {
    if (!this.canGoNext()) return;
    this.applyMappingAndBuildRows();
    this.next();
  }

  handleSelectFile(event: Event): void {
    this.file = (event.target as HTMLInputElement).files?.[0];
    if (!this.file) {
      this.fileLoaded = false;
      return;
    }

    const fileTypeLimit = UtilHelper.getFileTypeLimit(this.file.type);
    if (fileTypeLimit && this.file.size > fileTypeLimit.maxSize) {
      this.openModalAlert(fileTypeLimit.alertTitle, fileTypeLimit.alertMessage);
      this.fileLoaded = false;
      return;
    }

    this.fileLoaded = true;

    try {
      this.loading = true;
      Promise.all([
        this.file.text(),
        this.firebaseService.uploadFile(this.file, `${this.file?.name}_${Date.now()}`, UploadTypeEnum.CSV),
      ])
        .then(([csvText, { url }]) => {
          this.rawCsvText = csvText;
          this.url = url;
        })
        .catch(() => this.alertService.error(this.t('kanban.cardUpload.fileProcessError')))
        .finally(() => (this.loading = false));
    } catch {
      this.alertService.error(this.t('kanban.cardUpload.fileProcessError'));
      this.loading = false;
    }
  }

  clearFile(): void {
    this.file = undefined;
    this.fileLoaded = false;
    this.url = '';
    this.rawCsvText = undefined;
    this.rawRows = [];
    this.rawHeaders = [];
    this.mappings = [];
    this.csvContent = [];
    if (this.inputFile?.nativeElement) {
      this.inputFile.nativeElement.value = '';
    }
  }

  save(): void {
    const errorCount = this.getErrorCount();
    const validCount = this.csvContent.length - errorCount;

    if (validCount === 0) {
      this.alertService.error(this.t('kanban.cardUpload.noValidOpportunity'));
      return;
    }

    const idFunnel = this.formGroup.get('funnel')?.value;
    if (!idFunnel) {
      this.alertService.error(this.t('kanban.cardUpload.selectFunnelToImport'));
      return;
    }

    const mappings = Object.fromEntries(
      this.mappings
        .filter(m => m.mappedTo !== 'ignore')
        .map(m => [m.mappedTo, m.csvColumn]),
    );

    const importation = <Importation>(<unknown>{
      type: ImportationTypeEnum.CARD,
      filename: this.file?.name,
      contentType: this.file?.type,
      url: this.url,
      metadata: {
        totalSend: this.csvContent.length,
        totalExists: 0,
        totalInserted: 0,
        totalUpdated: 0,
        totalError: errorCount,
        mappings,
        params: { idFunnel },
      }
    });

    this.loadingContent = true;
    this.importationService.create(importation)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.alertService.success(this.t('kanban.cardUpload.importScheduledSuccess')),
        error: () => {
          this.alertService.error(this.t('kanban.cardUpload.importScheduleError'));
          this.loadingContent = false;
        },
        complete: () => this.dialogRef.close(true),
      });
  }

  getFunnels(): void {
    this.funnelService.getList().pipe(takeUntil(this.destroy$)).subscribe({
      next: (funnels) => this.funnelList = funnels,
      error: () => this.alertService.error(this.t('kanban.cardUpload.funnelListLoadError')),
    });
  }

  applyMappingAndBuildRows(): void {
    const mappingMap = new Map<CardField, string>(
      this.mappings.map(m => [m.mappedTo, m.csvColumn] as const),
    );

    const colName = mappingMap.get('name');
    const colPhone = mappingMap.get('phone');
    const colCustomer = mappingMap.get('customer');

    this.csvContent = this.rawRows.map((row: Record<string, any>, index: number) => {
      const cell = (col?: string) => (col ? (row[col] ?? '') : '').toString().trim();

      const name = cell(colName);
      const phone = cell(colPhone).replace(/\D+/g, '');
      const customer = cell(colCustomer);

      const hasError = !name || !phone || !customer;

      return {
        name,
        phone,
        customer,
        hasError,
        rowIndex: index + 2,
      };
    });

    this.currentPage = 0;
    this.updatePaginatedContent();
  }

  parseCsvAndStoreRaw(): void {
    if (!this.rawCsvText || typeof this.rawCsvText !== 'string') {
      this.openModalAlert(this.t('kanban.cardUpload.missingFileTitle'), this.t('kanban.cardUpload.missingFileMessage'));
      return;
    }

    Papa.parse(this.rawCsvText, {
      header: true,
      skipEmptyLines: true,
      complete: (results: Papa.ParseResult<any>) => {
        const rows = (results.data as any[]).filter(
          r => !!r && Object.keys(r).length > 1,
        );
        this.rawRows = rows;
        this.rawHeaders = results.meta.fields || Object.keys(rows[0] || {});
        this.buildDefaultMappings();
      },
      error: () => {
        this.alertService.error(this.t('kanban.cardUpload.csvProcessError'));
      },
    });
  }

  onMappingChange(changedIndex: number, newTarget: CardField): void {
    if (newTarget !== 'ignore') {
      this.mappings.forEach((m, i) => {
        if (i !== changedIndex && m.mappedTo === newTarget) {
          m.mappedTo = 'ignore';
        }
      });
    }
    this.mappings[changedIndex].mappedTo = newTarget;
  }

  isMappingValid(): boolean {
    return (['name', 'phone', 'customer'] as CardField[]).every(field =>
      this.mappings.some(m => m.mappedTo === field) && !!this.formGroup.get('funnel')?.value,
    );
  }

  formatBytes(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB'];
    let index = 0;

    while (bytes >= 1024 && index < units.length - 1) {
      bytes /= 1024;
      index++;
    }

    return index === 0 ? `${bytes} ${units[index]}` : `${bytes.toFixed(2)} ${units[index]}`;
  }

  getErrorCount(): number {
    return this.csvContent.filter(row => row.hasError).length;
  }

  removeAllErrors(): void {
    this.csvContent = this.csvContent.filter(row => !row.hasError);
    this.currentPage = 0;
    this.updatePaginatedContent();
  }

  toggleShowOnlyErrors(): void {
    this.showOnlyErrors = !this.showOnlyErrors;
    this.currentPage = 0;
    this.updatePaginatedContent();
  }

  delete(item: any): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, { width: '500px' });
    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.csvContent = this.csvContent.filter(i => i.rowIndex !== item.rowIndex);
        this.updatePaginatedContent();
      }
    });
  }

  handlePage(event: any): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePaginatedContent();
  }

  updatePaginatedContent(): void {
    let data = this.csvContent;
    if (this.showOnlyErrors) {
      data = data.filter(row => row.hasError);
    }
    const start = this.currentPage * this.pageSize;
    this.paginatedContent = data.slice(start, start + this.pageSize);
  }

  close(): void {
    this.dialogRef.close();
  }

  private buildDefaultMappings(): void {
    let headers = [...this.rawHeaders];
    let dataStart = 0;

    if (headers.every(h => /^\d+$/.test(h)) && this.rawRows.length) {
      const firstRow = this.rawRows[0];
      const firstValues = headers.map(h => String(firstRow[h] ?? '').trim());

      const looksLikeHeader = firstValues.some(v =>
        /(telefone|celular|whats|nome|name|cliente|customer|oportunidade|deal)/i.test(v),
      );

      if (looksLikeHeader) {
        headers = firstValues.map((v, i) => v || `col_${i + 1}`);
        dataStart = 1;
      }
    }

    this.mappings = headers.map((col, index) => {
      let sample = '';

      for (let r = dataStart; r < this.rawRows.length; r++) {
        const row = this.rawRows[r];
        const val = row[col] ?? row[this.rawHeaders[index]];
        if (val) {
          sample = String(val).trim();
          break;
        }
      }

      return {
        csvColumn: col,
        sample,
        mappedTo: this.inferField(col),
      };
    });
  }

  private inferField(columnName: string): CardField {
    const c = (columnName || '').toLowerCase().trim();

    if (/(telefone|celular|phone|whats|zap|numero|número)/.test(c)) return 'phone';
    if (/(cliente|customer|contato|contact)/.test(c)) return 'customer';
    if (/(oportunidade|opportunity|deal|negocio|negócio|titulo|título|nome|name)/.test(c)) return 'name';
    return 'ignore';
  }

  private openModalAlert(title: string, message: string): void {
    const dialogRefAlert = this.dialog.open(AlertModalComponent, {
      width: '600px',
      maxHeight: '300px',
      data: { title, message },
    });
    dialogRefAlert.afterClosed().subscribe(() => {
      if (this.inputFile?.nativeElement) this.inputFile.nativeElement.value = '';
    });
  }
}
